import React, { useState, useEffect } from 'react';
import { ChevronLeft } from 'lucide-react';

const PerformanceMetrics = () => {
  const [activeUsers, setActiveUsers] = useState(247);
  const [reqPerMin, setReqPerMin] = useState(142);
  const [avgResponse, setAvgResponse] = useState('1.85s');
  const [timeRange, setTimeRange] = useState('24h');
  const [feedEntries, setFeedEntries] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [heatmapData, setHeatmapData] = useState([]);

  // Update metrics every 3 seconds
  useEffect(() => {
    const updateMetrics = () => {
      setActiveUsers(Math.floor(200 + Math.random() * 100));
      setReqPerMin(Math.floor(120 + Math.random() * 40));
      setAvgResponse((1.5 + Math.random() * 0.8).toFixed(2) + 's');
    };

    const interval = setInterval(updateMetrics, 3000);
    return () => clearInterval(interval);
  }, []);

  // Generate response time chart data
  const generateChartData = () => {
    return Array.from({ length: 24 }, () => ({
      height: 30 + Math.random() * 60,
      value: (1.2 + Math.random() * 1.5).toFixed(1) + 's'
    }));
  };

  // Generate traffic heatmap data
  const generateHeatmapData = () => {
    return Array.from({ length: 24 }, (_, i) => {
      const intensity = Math.random();
      let heatClass = 'heat-low';
      if (intensity > 0.8) heatClass = 'heat-critical';
      else if (intensity > 0.6) heatClass = 'heat-high';
      else if (intensity > 0.3) heatClass = 'heat-medium';
      
      return {
        class: heatClass,
        title: `${i}:00 - ${Math.floor(intensity * 1000)} requêtes`
      };
    });
  };

  // Initialize chart and heatmap data
  useEffect(() => {
    setChartData(generateChartData());
    setHeatmapData(generateHeatmapData());
  }, [timeRange]);

  // Add live feed entries
  useEffect(() => {
    const addFeedEntry = () => {
      const time = new Date().toLocaleTimeString();
      const methods = ['GET', 'POST'];
      const endpoints = ['/query', '/health', '/feedback', '/stats'];
      const statuses = [200, 200, 200, 400, 500];
      
      const method = methods[Math.floor(Math.random() * methods.length)];
      const endpoint = endpoints[Math.floor(Math.random() * endpoints.length)];
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const duration = (Math.random() * 2000).toFixed(0);
      
      const newEntry = {
        id: Date.now() + Math.random(),
        time,
        method,
        endpoint,
        status,
        duration
      };
      
      setFeedEntries(prev => [newEntry, ...prev].slice(0, 20));
    };

    // Initial feed entries
    for (let i = 0; i < 10; i++) {
      setTimeout(() => addFeedEntry(), i * 100);
    }

    const interval = setInterval(addFeedEntry, 1500);
    return () => clearInterval(interval);
  }, []);

  // Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.metric-card').forEach(card => {
      card.style.opacity = '0';
      card.style.transform = 'translateY(20px)';
      card.style.transition = 'all 0.6s ease-out';
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, []);

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
  };

  return (
    <>
      <style>{`
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: #0a0a0a;
            color: #ffffff;
            line-height: 1.6;
        }
        
        /* Navigation */
        nav {
            position: fixed;
            top: 0;
            width: 100%;
            background: rgba(10, 10, 10, 0.95);
            backdrop-filter: blur(10px);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            z-index: 1000;
            padding: 1rem 2rem;
        }
        
        .nav-container {
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        
        .back-link {
            color: #667eea;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            transition: gap 0.3s;
        }
        
        .back-link:hover {
            gap: 1rem;
        }
        
        /* Main Container */
        .container {
            max-width: 1400px;
            margin: 0 auto;
            padding: 100px 2rem 2rem;
        }
        
        /* Header */
        .page-header {
            text-align: center;
            margin-bottom: 4rem;
        }
        
        .page-title {
            font-size: 3.5rem;
            margin-bottom: 1rem;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }
        
        .page-subtitle {
            font-size: 1.3rem;
            color: #888;
        }
        
        /* Real-time Status */
        .status-bar {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 1.5rem;
            margin-bottom: 3rem;
            display: flex;
            justify-content: space-around;
            align-items: center;
            flex-wrap: wrap;
            gap: 2rem;
        }
        
        .status-item {
            text-align: center;
        }
        
        .status-indicator {
            width: 12px;
            height: 12px;
            background: #67e8a9;
            border-radius: 50%;
            display: inline-block;
            margin-right: 0.5rem;
            animation: pulse 2s ease-in-out infinite;
        }
        
        @keyframes pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.2); }
        }
        
        .status-label {
            font-weight: 600;
            margin-bottom: 0.3rem;
        }
        
        .status-value {
            font-size: 1.5rem;
            color: #667eea;
            font-weight: bold;
        }
        
        /* Dashboard Grid */
        .dashboard-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
            gap: 2rem;
            margin-bottom: 3rem;
        }
        
        .metric-card {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            transition: all 0.3s;
            position: relative;
            overflow: hidden;
        }
        
        .metric-card:hover {
            transform: translateY(-5px);
            border-color: rgba(102, 126, 234, 0.3);
        }
        
        .metric-header {
            display: flex;
            justify-content: space-between;
            align-items: start;
            margin-bottom: 1.5rem;
        }
        
        .metric-title {
            font-size: 1.2rem;
            font-weight: 600;
        }
        
        .metric-badge {
            padding: 0.3rem 0.8rem;
            background: rgba(102, 126, 234, 0.2);
            border-radius: 6px;
            font-size: 0.8rem;
            color: #667eea;
        }
        
        .metric-value {
            font-size: 3rem;
            font-weight: bold;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 0.5rem;
        }
        
        .metric-subtitle {
            color: #888;
            font-size: 0.9rem;
            margin-bottom: 1rem;
        }
        
        .metric-trend {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: #67e8a9;
            font-size: 0.9rem;
        }
        
        .trend-up { color: #67e8a9; }
        .trend-down { color: #f56565; }
        
        /* Charts Container */
        .chart-container {
            background: rgba(255, 255, 255, 0.03);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 16px;
            padding: 2rem;
            margin-bottom: 2rem;
        }
        
        .chart-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
        }
        
        .chart-title {
            font-size: 1.5rem;
            font-weight: 600;
        }
        
        .chart-controls {
            display: flex;
            gap: 1rem;
        }
        
        .time-selector {
            padding: 0.5rem 1rem;
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 6px;
            color: white;
            cursor: pointer;
            transition: all 0.3s;
        }
        
        .time-selector:hover {
            background: rgba(255, 255, 255, 0.1);
        }
        
        .time-selector.active {
            background: rgba(102, 126, 234, 0.2);
            border-color: rgba(102, 126, 234, 0.5);
            color: #667eea;
        }
        
        /* Chart Styles */
        .chart-area {
            height: 300px;
            position: relative;
            margin-bottom: 1rem;
        }
        
        .chart-bar {
            position: absolute;
            bottom: 0;
            background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
            border-radius: 4px 4px 0 0;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .chart-bar:hover {
            opacity: 0.8;
            transform: scaleY(1.05);
        }
        
        .chart-bar-value {
            position: absolute;
            top: -25px;
            left: 50%;
            transform: translateX(-50%);
            font-size: 0.8rem;
            font-weight: bold;
            opacity: 0;
            transition: opacity 0.3s;
        }
        
        .chart-bar:hover .chart-bar-value {
            opacity: 1;
        }
        
        .chart-labels {
            display: flex;
            justify-content: space-between;
            color: #888;
            font-size: 0.8rem;
        }
        
        /* Heatmap */
        .heatmap-container {
            display: grid;
            grid-template-columns: repeat(24, 1fr);
            gap: 2px;
            margin: 2rem 0;
        }
        
        .heatmap-cell {
            aspect-ratio: 1;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 2px;
            transition: all 0.3s;
            cursor: pointer;
        }
        
        .heatmap-cell:hover {
            transform: scale(1.2);
            border: 1px solid rgba(102, 126, 234, 0.5);
        }
        
        .heat-low { background: rgba(102, 126, 234, 0.2); }
        .heat-medium { background: rgba(102, 126, 234, 0.5); }
        .heat-high { background: rgba(102, 126, 234, 0.8); }
        .heat-critical { background: rgba(245, 101, 101, 0.8); }
        
        /* Performance Table */
        .performance-table {
            width: 100%;
            border-collapse: collapse;
            margin: 2rem 0;
        }
        
        .performance-table th,
        .performance-table td {
            padding: 1rem;
            text-align: left;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .performance-table th {
            background: rgba(102, 126, 234, 0.1);
            font-weight: 600;
        }
        
        .performance-table tr:hover {
            background: rgba(255, 255, 255, 0.05);
        }
        
        .endpoint-badge {
            padding: 0.2rem 0.6rem;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        /* Alerts Section */
        .alerts-section {
            background: rgba(237, 137, 54, 0.1);
            border: 1px solid rgba(237, 137, 54, 0.3);
            border-radius: 16px;
            padding: 2rem;
            margin: 2rem 0;
        }
        
        .alert-item {
            display: flex;
            align-items: center;
            gap: 1rem;
            padding: 1rem 0;
            border-bottom: 1px solid rgba(237, 137, 54, 0.2);
        }
        
        .alert-item:last-child {
            border-bottom: none;
        }
        
        .alert-icon {
            width: 40px;
            height: 40px;
            background: rgba(237, 137, 54, 0.2);
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #ed8936;
        }
        
        .alert-content {
            flex: 1;
        }
        
        .alert-title {
            font-weight: 600;
            margin-bottom: 0.2rem;
        }
        
        .alert-time {
            font-size: 0.8rem;
            color: #888;
        }
        
        /* Live Feed */
        .live-feed {
            background: #1a1a1a;
            border: 1px solid #333;
            border-radius: 12px;
            padding: 1.5rem;
            height: 400px;
            overflow-y: auto;
            font-family: monospace;
            font-size: 0.9rem;
        }
        
        .feed-entry {
            padding: 0.5rem 0;
            border-bottom: 1px solid #333;
            opacity: 0;
            animation: fadeIn 0.3s ease forwards;
        }
        
        @keyframes fadeIn {
            to {
                opacity: 1;
            }
        }
        
        .feed-time {
            color: #667eea;
            margin-right: 1rem;
        }
        
        .feed-method {
            color: #82aaff;
            margin-right: 1rem;
        }
        
        .feed-status-200 { color: #67e8a9; }
        .feed-status-400 { color: #ed8936; }
        .feed-status-500 { color: #f56565; }
        
        /* Responsive */
        @media (max-width: 768px) {
            .dashboard-grid {
                grid-template-columns: 1fr;
            }
            
            .status-bar {
                flex-direction: column;
            }
            
            .chart-controls {
                flex-direction: column;
            }
        }
      `}</style>

      {/* Navigation */}
      <nav>
        <div className="nav-container">
          <a href="index.html" className="back-link">
            <ChevronLeft size={16} /> Retour à l'accueil
          </a>
          <div>Métriques de Performance</div>
        </div>
      </nav>
      
      {/* Main Content */}
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <h1 className="page-title">Dashboard Performance</h1>
          <p className="page-subtitle">Monitoring temps réel et analytics de l'Agent RAG Hybride</p>
        </div>
        
        {/* Real-time Status Bar */}
        <div className="status-bar">
          <div className="status-item">
            <div className="status-label">
              <span className="status-indicator"></span>
              System Status
            </div>
            <div className="status-value">Operational</div>
          </div>
          <div className="status-item">
            <div className="status-label">Active Users</div>
            <div className="status-value">{activeUsers}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Req/min</div>
            <div className="status-value">{reqPerMin}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Avg Response</div>
            <div className="status-value">{avgResponse}</div>
          </div>
          <div className="status-item">
            <div className="status-label">Uptime</div>
            <div className="status-value">99.97%</div>
          </div>
        </div>
        
        {/* Key Metrics Grid */}
        <div className="dashboard-grid">
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">Précision Globale</h3>
              <span className="metric-badge">AI Quality</span>
            </div>
            <div className="metric-value">95.2%</div>
            <div className="metric-subtitle">Sur 10k dernières requêtes</div>
            <div className="metric-trend trend-up">
              ↑ +2.3% vs semaine dernière
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">Latence P95</h3>
              <span className="metric-badge">Performance</span>
            </div>
            <div className="metric-value">2.3s</div>
            <div className="metric-subtitle">95% des requêtes &lt; 2.3s</div>
            <div className="metric-trend trend-up">
              ↑ -15% amélioration
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">Cache Hit Rate</h3>
              <span className="metric-badge">Efficiency</span>
            </div>
            <div className="metric-value">64%</div>
            <div className="metric-subtitle">Réponses servies du cache</div>
            <div className="metric-trend trend-up">
              ↑ +5% cette semaine
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">Requêtes Totales</h3>
              <span className="metric-badge">Volume</span>
            </div>
            <div className="metric-value">45.2k</div>
            <div className="metric-subtitle">Dernières 24 heures</div>
            <div className="metric-trend trend-up">
              ↑ +12% vs jour précédent
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">F1 Score</h3>
              <span className="metric-badge">Search Quality</span>
            </div>
            <div className="metric-value">93.8%</div>
            <div className="metric-subtitle">Précision × Rappel</div>
            <div className="metric-trend trend-up">
              ↑ +1.2% optimisation
            </div>
          </div>
          
          <div className="metric-card">
            <div className="metric-header">
              <h3 className="metric-title">Satisfaction User</h3>
              <span className="metric-badge">Feedback</span>
            </div>
            <div className="metric-value">4.7/5</div>
            <div className="metric-subtitle">Basé sur 1.2k feedbacks</div>
            <div className="metric-trend trend-up">
              ↑ +0.3 points
            </div>
          </div>
        </div>
        
        {/* Response Time Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Temps de Réponse par Heure</h3>
            <div className="chart-controls">
              <button 
                className={`time-selector ${timeRange === '24h' ? 'active' : ''}`}
                onClick={() => handleTimeRangeChange('24h')}
              >
                24h
              </button>
              <button 
                className={`time-selector ${timeRange === '7d' ? 'active' : ''}`}
                onClick={() => handleTimeRangeChange('7d')}
              >
                7j
              </button>
              <button 
                className={`time-selector ${timeRange === '30d' ? 'active' : ''}`}
                onClick={() => handleTimeRangeChange('30d')}
              >
                30j
              </button>
            </div>
          </div>
          <div className="chart-area">
            {chartData.map((bar, index) => (
              <div 
                key={index}
                className="chart-bar"
                style={{
                  height: `${bar.height}%`,
                  width: 'calc(100% / 24 - 4px)',
                  left: `${(index * 100 / 24)}%`
                }}
              >
                <div className="chart-bar-value">{bar.value}</div>
              </div>
            ))}
          </div>
          <div className="chart-labels">
            <span>00:00</span>
            <span>06:00</span>
            <span>12:00</span>
            <span>18:00</span>
            <span>24:00</span>
          </div>
        </div>
        
        {/* Traffic Heatmap */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Heatmap Trafic (24h)</h3>
          </div>
          <div className="heatmap-container">
            {heatmapData.map((cell, index) => (
              <div 
                key={index}
                className={`heatmap-cell ${cell.class}`}
                title={cell.title}
              />
            ))}
          </div>
          <p style={{textAlign: 'center', color: '#888', marginTop: '1rem'}}>
            Chaque cellule = 1 heure | Intensité = Volume de requêtes
          </p>
        </div>
        
        {/* Search Methods Distribution */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Distribution des Méthodes de Recherche</h3>
          </div>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '2rem', textAlign: 'center'}}>
            <div>
              <div style={{fontSize: '2.5rem', marginBottom: '0.5rem'}}>🧠</div>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#667eea'}}>85%</div>
              <div style={{color: '#888'}}>Vectorielle</div>
            </div>
            <div>
              <div style={{fontSize: '2.5rem', marginBottom: '0.5rem'}}>🔍</div>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#667eea'}}>72%</div>
              <div style={{color: '#888'}}>Keywords</div>
            </div>
            <div>
              <div style={{fontSize: '2.5rem', marginBottom: '0.5rem'}}>🕸️</div>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#667eea'}}>23%</div>
              <div style={{color: '#888'}}>Graphe</div>
            </div>
            <div>
              <div style={{fontSize: '2.5rem', marginBottom: '0.5rem'}}>📋</div>
              <div style={{fontSize: '2rem', fontWeight: 'bold', color: '#667eea'}}>15%</div>
              <div style={{color: '#888'}}>Metadata</div>
            </div>
          </div>
        </div>
        
        {/* Performance by Endpoint */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Performance par Endpoint</h3>
          </div>
          <table className="performance-table">
            <thead>
              <tr>
                <th>Endpoint</th>
                <th>Requêtes/h</th>
                <th>Latence Moy.</th>
                <th>P95</th>
                <th>Erreurs</th>
                <th>Tendance</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="endpoint-badge">POST /query</span></td>
                <td>1,847</td>
                <td>1.92s</td>
                <td>2.8s</td>
                <td style={{color: '#67e8a9'}}>0.2%</td>
                <td className="trend-up">↑</td>
              </tr>
              <tr>
                <td><span className="endpoint-badge">GET /health</span></td>
                <td>3,200</td>
                <td>12ms</td>
                <td>18ms</td>
                <td style={{color: '#67e8a9'}}>0%</td>
                <td>→</td>
              </tr>
              <tr>
                <td><span className="endpoint-badge">POST /feedback</span></td>
                <td>324</td>
                <td>85ms</td>
                <td>120ms</td>
                <td style={{color: '#67e8a9'}}>0.1%</td>
                <td className="trend-up">↑</td>
              </tr>
              <tr>
                <td><span className="endpoint-badge">GET /stats</span></td>
                <td>98</td>
                <td>234ms</td>
                <td>450ms</td>
                <td style={{color: '#ed8936'}}>1.2%</td>
                <td className="trend-down">↓</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Active Alerts */}
        <div className="alerts-section">
          <h3 style={{marginBottom: '1.5rem'}}>⚠️ Alertes Actives</h3>
          <div className="alert-item">
            <div className="alert-icon">🔥</div>
            <div className="alert-content">
              <div className="alert-title">Latence élevée détectée</div>
              <div className="alert-time">Il y a 12 minutes - P95 &gt; 3s sur /query</div>
            </div>
          </div>
          <div className="alert-item">
            <div className="alert-icon">💾</div>
            <div className="alert-content">
              <div className="alert-title">Cache Redis utilisation &gt; 85%</div>
              <div className="alert-time">Il y a 47 minutes - Considérer scaling</div>
            </div>
          </div>
        </div>
        
        {/* Live Feed */}
        <div className="chart-container">
          <div className="chart-header">
            <h3 className="chart-title">Feed Temps Réel</h3>
          </div>
          <div className="live-feed">
            {feedEntries.map(entry => (
              <div key={entry.id} className="feed-entry">
                <span className="feed-time">{entry.time}</span>
                <span className="feed-method">{entry.method}</span>
                <span>{entry.endpoint}</span>
                <span className={`feed-status-${entry.status}`} style={{marginLeft: '1rem'}}>
                  {entry.status}
                </span>
                <span style={{marginLeft: '1rem', color: '#666'}}>{entry.duration}ms</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PerformanceMetrics;